/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import {
  Text,
  Placeholder,
  withSitecoreContext,
} from '@sitecore-jss/sitecore-jss-react';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import throttle from 'lodash/throttle';
import { compose } from 'lodash/fp';
import { Container, Column, Row, MediaQueries } from '../../../styles/Grid';
import { hideElementCss, showElementCss } from '../../../utils/responsive';
import { getPrefilledSolutionValue } from '../../../utils/getPrefilledSolutionValue';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import Spacer from '../../ui/Spacer';
import { withNamespaces } from 'react-i18next';
import createButtonCss from '../../ui/Button';
import DotComponent from '../../ui/DotComponent';
import OpenEnquiryFormButton from '../../ui/OpenEnquiryFormButton';
import { colorsPalette } from '../../../styles/colors';
import { componentHasStickyLabel } from '../StickyBar';
import Picture from '../../ui/Picture';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import { NavConsumer, navbar_finite_state } from '../../../contexts/NavContext';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { processButtonData } from '../../../utils/analytics';
import LinkWrapper from '../../ui/LinkWrapper';
import { StickyBarConsumer } from '../../../contexts/StickyBarContext';
import { withConsumer } from '../../../contexts';
import * as styles from './index.styles';

const mainImageSizes = {
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 375,
    height: 296,
    gravity: 'auto:subject',
    aspectRatio: '16:9',
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '16:9',
    width: 727,
    height: 408,
    gravity: 'auto:subject',
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '16:9',
    width: 556,
    height: 312,
    gravity: 'auto:subject',
  },
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '16:9',
    width: 556,
    height: 312,
    gravity: 'auto:subject',
  },
};

const mainImageSubsolutionSizes = {
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 375,
    height: 296,
    gravity: 'auto:subject',
    aspectRatio: '16:9',
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 727,
    height: 408,
    gravity: 'auto:subject',
    aspectRatio: '16:9',
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 597,
    height: 856,
    gravity: 'auto:subject',
    aspectRatio: '16:9',
  },
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 597,
    height: 856,
    gravity: 'auto:subject',
    aspectRatio: '16:9',
  },
};

const leftImageSizes = {
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '3:4',
    gravity: 'auto:subject',
  },
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '3:4',
    width: 196,
    height: 261,
    gravity: 'auto:subject',
  },
};

const rightImageSizes = {
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '16:9',
    gravity: 'auto:subject',
  },
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '16:9',
    width: 421,
    height: 236,
    gravity: 'auto:subject',
  },
};

export const defaultFields = {
  BackgroundColor: {
    fields: {
      Value: {
        value: 'brown10',
      },
    },
  },
  Headline: { value: '' },
  Subtitle: { value: '' },
  Description: {
    value: '',
  },
  HeroImage1: {
    value: {
      src: '',
      alt: '',
    },
  },
  HeroImage2: {
    value: {
      src: '',
      alt: '',
    },
  },
  PricingAndAvailabilityText: {
    value: '',
  },
  HeroImage3: {
    value: {
      src: '',
      alt: '',
    },
  },
  ProtonName: { value: '' },
  PhoneNumber: { value: '' },
  SolutionHeroImage1: {
    value: {
      src: '',
      alt: '',
    },
  },
  SolutionHeroImage2: {
    value: {
      src: '',
      alt: '',
    },
  },
  SolutionHeroImage3: {
    value: {
      src: '',
      alt: '',
    },
  },
  GetInTouchCTA: {
    value: {
      href: '',
      linktype: '',
      text: '',
      anchor: '',
      querystring: '',
      target: '',
    },
  },
};

const MainImageTouchDevices = ({ imageData }) => {
  return (
    <Picture
      image={imageData}
      imageSizes={mainImageSizes}
      pictureCss={[styles.mainImageCss, hideElementCss(MediaQueries.lgAndUp)]}
      instantLoad
    />
  );
};

const MainImageLg = ({ imageData, isOneImageFromData }) => {
  return (
    <Picture
      image={imageData}
      imageSizes={
        isOneImageFromData ? mainImageSubsolutionSizes : mainImageSizes
      }
      pictureCss={styles.mainImageLgCss(isOneImageFromData)}
      instantLoad
    />
  );
};

const LeftBottomImage = ({ imageData }) => {
  return (
    <Picture
      image={imageData}
      imageSizes={leftImageSizes}
      pictureCss={styles.leftBottomImageCss}
      instantLoad
    />
  );
};

const RightBottomImage = ({ imageData }) => {
  return (
    <Picture
      image={imageData}
      imageSizes={rightImageSizes}
      pictureCss={styles.rightBottomImageCss}
      instantLoad
    />
  );
};

export class SolutionHero extends SafePureComponent {
  stickyBarTriggerRef = React.createRef();

  componentDidMount() {
    window.addEventListener('scroll', this.onScrollHandler);
    this.setStickyBarLayout();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScrollHandler = throttle(() => {
    const { stickyBarContext } = this.props;
    const rect = this.stickyBarTriggerRef.current.getBoundingClientRect();
    const bottom = rect.bottom;

    if (bottom < 0) {
      stickyBarContext.showStickyBar();
    } else {
      stickyBarContext.hideStickyBar();
    }
  }, 80);

  setStickyBarLayout() {
    const { stickyBarContext } = this.props;
    const hideSecondaryCTA = get(
      this.props,
      'fields.HideSecondaryCTA.value',
      false,
    );

    if (hideSecondaryCTA) {
      stickyBarContext.activateOneButtonLayout();
    }
  }

  renderGetInTouchCTA(linkObject, componentName) {
    const prefilledSolutionValue = getPrefilledSolutionValue(
      this.props.sitecoreContext,
    );

    const openEnquiryFormButtonProps = {
      linkObject,
      prefilledValues: {
        Solution: prefilledSolutionValue,
      },
      customCss: styles.getInTouchCss,
    };

    return (
      <OpenEnquiryFormButton
        {...openEnquiryFormButtonProps}
        componentName={componentName}
      />
    );
  }

  renderSecondaryCTA(fields, componentName) {
    const { SecondaryCTA, HideSecondaryCTA } = fields;
    const { t } = this.props;
    const secondaryCTALink = get(SecondaryCTA, 'value.href');
    const hasSecondaryCTALink =
      !!secondaryCTALink && secondaryCTALink !== 'http://';
    const secondaryCTALabel = get(SecondaryCTA, 'value.text', t('FindCTA'));
    const shouldHideSecondaryCTA = get(HideSecondaryCTA, 'value', false);

    if (shouldHideSecondaryCTA) return null;

    if (hasSecondaryCTALink) {
      return (
        <LinkWrapper
          field={SecondaryCTA}
          type={'secondary'}
          css={styles.solutionInCtaCss}
          componentName={componentName}
          isGTMTrackable
        />
      );
    }

    return (
      <NavConsumer>
        {({ openModal }) => (
          <button
            data-id={navbar_finite_state.SEARCH}
            onClick={e => {
              processButtonData(componentName, secondaryCTALabel);
              openModal(e);
            }}
            css={[createButtonCss('secondary'), styles.solutionInCtaCss]}
          >
            {secondaryCTALabel}
          </button>
        )}
      </NavConsumer>
    );
  }

  safeRender() {
    const { props } = this;
    const { fields: fieldsProp } = props;
    const fields = defaultsDeep(fieldsProp, defaultFields);
    const hasStickyLabel = componentHasStickyLabel(props);
    const dataStickyProps = hasStickyLabel
      ? { 'data-sticky-id': get(props, 'rendering.uid') }
      : {};
    const {
      ProtonName,
      Subtitle,
      Description,
      SolutionHeroImage1,
      SolutionHeroImage2,
      SolutionHeroImage3,
      BackgroundColor,
      GetInTouchCTA,
    } = fields;
    const bgColor = get(fields, 'BackgroundColor.fields.Value.value', 'white');
    const isTheSolutionHeroImage2Empty = !get(
      SolutionHeroImage2,
      'value.src',
      '',
    );
    const isTheSolutionHeroImage3Empty = !get(
      SolutionHeroImage3,
      'value.src',
      '',
    );
    const isOneImageFromData =
      isTheSolutionHeroImage2Empty && isTheSolutionHeroImage3Empty;
    const shouldShowPhoneNumber = !get(fields, 'HidePhoneNumber.value', false);
    const componentName = get(props, 'rendering.componentName', '');

    return (
      <div
        data-hero
        css={{ backgroundColor: colorsPalette[bgColor] }}
        {...dataStickyProps}
      >
        <MainImageTouchDevices imageData={SolutionHeroImage1} />
        <Spacer
          backgroundColor={BackgroundColor}
          customCss={styles.customSpacerCss}
          marginCss={styles.containerMarginCss}
          paddingCss={styles.containerPaddingCss}
        >
          <Container>
            <Row>
              <Column cols={{ sm: 6, md: 8, lg: 6, xl: 5 }} offsets={{ xl: 1 }}>
                <h1 css={styles.headlineCss}>
                  <Text field={ProtonName} />
                  <DotComponent />
                </h1>
                <p css={styles.subtitleCss}>
                  <Text field={Subtitle} />
                </p>
                <p css={styles.descriptionCss}>
                  <Text field={Description} />
                </p>
                {shouldShowPhoneNumber && (
                  <div css={styles.contactCss}>
                    <Placeholder name="jss-phone" rendering={props.rendering} />
                  </div>
                )}
                <Row
                  ref={this.stickyBarTriggerRef}
                  data-testid="sticky-trigger"
                >
                  <Column
                    css={styles.ctasNotLgCss}
                    cols={{ sm: 6, md: 3, lg: [2, 5], xl: [2, 5] }}
                  >
                    {this.renderGetInTouchCTA(GetInTouchCTA, componentName)}
                  </Column>
                  <Column
                    css={styles.ctasNotLgCss}
                    cols={{ sm: 6, md: 3, lg: [2, 5], xl: [2, 5] }}
                  >
                    {this.renderSecondaryCTA(fields, componentName)}
                  </Column>
                  <Column
                    css={styles.ctasLgCss}
                    cols={{ lg: [4, 5] }}
                    data-testid="buttons-lg"
                  >
                    {this.renderGetInTouchCTA(GetInTouchCTA, componentName)}
                    {this.renderSecondaryCTA(fields, componentName)}
                  </Column>
                </Row>
              </Column>
              <Column
                css={showElementCss(MediaQueries.lgAndUp)}
                cols={{ sm: 6, md: 8, lg: 6 }}
              >
                <Row
                  css={[styles.fullHeight(isOneImageFromData), styles.rowFlex]}
                >
                  <Column
                    css={styles.fullHeight(isOneImageFromData)}
                    cols={{ lg: [6, 6] }}
                    offsets={{ lg: [1, 6] }}
                  >
                    <div
                      css={[
                        styles.mainImageLgContainerCss(isOneImageFromData),
                        styles.fullHeight(isOneImageFromData),
                      ]}
                    >
                      <MainImageLg
                        isOneImageFromData={isOneImageFromData}
                        imageData={SolutionHeroImage1}
                      />
                    </div>
                  </Column>
                  {!isOneImageFromData && (
                    <React.Fragment>
                      <Column
                        css={styles.leftBottomImageColumnCss}
                        cols={{ lg: [3, 6] }}
                      >
                        <div css={styles.leftBottomImageContainerCss}>
                          <LeftBottomImage imageData={SolutionHeroImage2} />
                        </div>
                      </Column>
                      <Column cols={{ lg: [3, 6] }}>
                        <div css={styles.rightBottomImageContainerCss}>
                          <RightBottomImage imageData={SolutionHeroImage3} />
                        </div>
                      </Column>
                    </React.Fragment>
                  )}
                </Row>
              </Column>
            </Row>
          </Container>
        </Spacer>
      </div>
    );
  }
}

SolutionHero.displayName = 'SolutionHero';

SolutionHero.defaultProps = {
  fields: defaultFields,
  t: key => key,
};

SolutionHero.propTypes = {
  fields: PropTypes.shape({
    BackgroundColor: PropTypes.shape({
      fields: PropTypes.shape({
        Value: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
    }),
    Headline: PropTypes.shape({
      value: PropTypes.string,
    }),
    Subtitle: PropTypes.shape({
      value: PropTypes.string,
    }),
    Description: PropTypes.shape({
      value: PropTypes.string,
    }),
    PricingAndAvailabilityText: PropTypes.shape({
      value: PropTypes.string,
    }),
    PhoneNumber: PropTypes.shape({
      value: PropTypes.string,
    }),
    ProtonName: PropTypes.shape({
      value: PropTypes.string,
    }),
    SolutionHeroImage1: PropTypes.shape({
      value: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        width: PropTypes.string,
        height: PropTypes.string,
      }),
    }),
    SolutionHeroImage2: PropTypes.shape({
      value: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        width: PropTypes.string,
        height: PropTypes.string,
      }),
    }),
    SolutionHeroImage3: PropTypes.shape({
      value: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        width: PropTypes.string,
        height: PropTypes.string,
      }),
    }),
  }),
  t: PropTypes.func,
};

export default compose(
  withNamespaces(),
  withSitecoreContext(),
  withConsumer(StickyBarConsumer, 'stickyBarContext'),
)(SolutionHero);
